import { Inject, Injectable, InjectionToken, Optional, Injector } from "@angular/core";
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest } from "@angular/common/http";
import { Observable } from "rxjs";
import { ApiPrefixInterceptor } from "@app/core/services/http/interceptors/api-prefix.interceptor";
import { ErrorHandlerInterceptor } from "@app/core/services/http/interceptors/error-handler.interceptor";
import { ResponseInterceptor } from "@app/core/services/http/interceptors/response.interceptor";
import { LoaderInterceptor } from "@app/core/services/http/interceptors/loader.interceptor";
import { RequestInterceptor } from "@app/core/services/http/interceptors/request.interceptor";
import { DisableRequestCachingInterceptor } from "@app/core/services/http/interceptors/disable-request-caching.interceptor";
import { NotifyInterceptor } from "@app/core/services/http/interceptors/notify.interceptor";
import { HttpParamsInterceptor } from "@app/core/services/http/interceptors/params.interceptor";
import { GetModelInterceptor } from "@app/core/services/http/interceptors/get-model.interceptor";
import { NodeJSAuthenticationInterceptor } from "@app/core/services/http/interceptors/node-js-authentication.interceptor";
import { NodeJSResponseInterceptor } from "@app/core/services/http/interceptors/node-js-response.interceptor";
import { LoginContext } from "@app/app-modules/login/login.model";
import { HttpClient as NgHttpClient } from "@angular/common/http";
import { EncodeContentTypeInterceptor } from "@app/core/services/http/interceptors/encode-content-type.interceptor";
import { LoginCredentialsInterceptor } from "@app/core/services/http/interceptors/login-credentials.interceptor";
import { ServiceWorkerBypassInterceptor } from "@app/core/services/http/interceptors/service-worker-bypass.interceptor";

export interface HttpClient extends NgHttpClient {
  /**
   * Skips activating the loader bar.
   * Call this for silent background calls
   * @param skip - Optional value to conditionally remove the loader bar
   *               false => Do not remove the loader for silent background process.
   *               true  => Remove the loader for silent background process.
   * @returns The new instance.
   */
  skipLoaderHandler(skip?: boolean): HttpClient;

  /**
   * Skips default error handler for this request.
   * @return The new instance.
   */
  skipErrorHandler(): HttpClient;

  /**
   * Skips default notification handler for this request.
   * @return The new instance.
   */
  skipNotifyHandler(): HttpClient;

  /**
   * Do not use API prefix for this request.
   * @return The new instance.
   */
  disableApiPrefix(): HttpClient;

  /**
   * Do not use USSI interceptors for request, response or headers.
   * @returns The new instance.
   */
  makeNonUSSICall(): HttpClient;

  /**
   * Prepare call for downloading a file. Reconfigures response behaviour.
   * @returns The new instance.
   */
  makeDownloadFileCall(): HttpClient;

  /**
   * Do not include token and language headers.
   * @returns The new instance.
   */
  removeDefaultHeaders(): HttpClient;

  /**
   * Do not include default encoding
   * @returns The new instance.
   */
  removeDefaultEncoding(): HttpClient;

  /**
   * Add url prefix.
   * Call this whenever a call to makeNonUSSICall() is made but still need
   * the AddPrefixIntercepter.
   * @returns The new instance.
   */
  addUrlPrefix(): HttpClient;

  /**
   * Add request intercept
   * Call this whenever a call to makeNonUSSICall() is made but still need headers
   * @returns The new instance.
   */
  addRequestInterceptor(): HttpClient;

  /**
   * Add USSI data query param to request
   * @returns The new instance.
   */
  addDataParams(data: object): HttpClient;

  /**
   * Get model
   * @returns The new instance.
   */
  getModel(): HttpClient;

  /**
   * Send in authentication parameters for PortalsPlusJS/Node JS project.
   * @returns The new instance.
   */
  addNodeJsAuth(refresh?: boolean): HttpClient;

  /**
   * Handle NodeJS Response
   * @returns The new instance.
   */
  addNodeJsResponse(): HttpClient;

  /**
   * Handle Login Credentials
   * @returns The new instance.
   */
  addLoginCredentials(data: LoginContext): HttpClient;

  /**
   * Add Ngsw Bypass header
   * @returns The new instance
   */
  addServiceWorkerBypass(): HttpClient;
}

// From @angular/common/http/src/interceptor: allows to chain interceptors
class HttpInterceptorHandler implements HttpHandler {
  constructor(
    private next: HttpHandler,
    private interceptor: HttpInterceptor
  ) {}

  handle(request: HttpRequest<any>): Observable<HttpEvent<any>> {
    return this.interceptor.intercept(request, this.next);
  }
}

/**
 * Allows to default dynamic interceptors that can be disabled with the HttpService extension.
 * Except for very specific needs, you should better configure these interceptors directly in the constructor below
 * for better readability.
 *
 * For static interceptors that should always be enabled (like ApiPrefixInterceptor), use the standard
 * HTTP_INTERCEPTORS token.
 */
export const HTTP_DYNAMIC_INTERCEPTORS = new InjectionToken<HttpInterceptor>("HTTP_DYNAMIC_INTERCEPTORS");

/**
 * Extends HttpClient with per request configuration using dynamic interceptors.
 */
@Injectable({
  providedIn: "root"
})
export class HttpService extends NgHttpClient {
  constructor(
    private injector: Injector,
    private httpHandler: HttpHandler,
    @Optional()
    @Inject(HTTP_DYNAMIC_INTERCEPTORS)
    private interceptors: HttpInterceptor[] = []
  ) {
    super(httpHandler);

    if (!this.interceptors) {
      // Configure default interceptors that can be disabled here
      this.interceptors = [
        this.injector.get(ApiPrefixInterceptor),
        this.injector.get(ErrorHandlerInterceptor),
        this.injector.get(ResponseInterceptor),
        this.injector.get(LoaderInterceptor),
        this.injector.get(RequestInterceptor),
        this.injector.get(EncodeContentTypeInterceptor),
        this.injector.get(DisableRequestCachingInterceptor),
        this.injector.get(NotifyInterceptor)
      ];
    }
  }

  skipLoaderHandler(skip: boolean = true): HttpClient {
    if (skip) {
      return this.removeInterceptor(LoaderInterceptor);
    }
    return this;
  }

  skipErrorHandler(): HttpClient {
    return this.removeInterceptor(ErrorHandlerInterceptor);
  }

  skipNotifyHandler(): HttpClient {
    return this.removeInterceptor(NotifyInterceptor);
  }

  disableApiPrefix(): HttpClient {
    return this.removeInterceptor(ApiPrefixInterceptor);
  }

  removeDefaultHeaders(): HttpClient {
    return this.removeInterceptor(RequestInterceptor);
  }

  removeDefaultEncoding(): HttpClient {
    return this.removeInterceptor(EncodeContentTypeInterceptor);
  }

  makeNonUSSICall(): HttpClient {
    return this.removeInterceptor(ApiPrefixInterceptor, RequestInterceptor, ResponseInterceptor);
  }

  makeDownloadFileCall(): HttpClient {
    const interceptor = this.injector.get(ResponseInterceptor).configure(true);
    return this.removeInterceptor(ResponseInterceptor).addInterceptor(interceptor);
  }

  addUrlPrefix() {
    return this.addInterceptor(new ApiPrefixInterceptor());
  }

  addRequestInterceptor() {
    return this.addInterceptor(new RequestInterceptor());
  }

  addDataParams(data: object) {
    const httpParamsInterceptor = this.injector.get(HttpParamsInterceptor).configure(data);
    return this.addInterceptor(httpParamsInterceptor);
  }

  getModel() {
    return this.addInterceptor(new GetModelInterceptor());
  }

  addNodeJsAuth(refresh?: boolean) {
    return this.addInterceptors([new NodeJSAuthenticationInterceptor(refresh), new NodeJSResponseInterceptor()]);
  }

  addNodeJsResponse() {
    return this.addInterceptor(new NodeJSResponseInterceptor());
  }

  addLoginCredentials(data: LoginContext) {
    return this.addInterceptor(new LoginCredentialsInterceptor(data));
  }

  addServiceWorkerBypass() {
    return this.addInterceptor(new ServiceWorkerBypassInterceptor());
  }

  // the original method to wire interceptors when triggering the request.
  override request(method?: any, url?: any, options?: any): any {
    const handler = this.interceptors.reduceRight((next, interceptor) => new HttpInterceptorHandler(next, interceptor), this.httpHandler);
    return new NgHttpClient(handler).request(method, url, options);
  }

  // Remove any number of Interceptors
  private removeInterceptor(...interceptorTypes: Function[]): HttpService {
    return new HttpService(
      this.injector,
      this.httpHandler,
      this.interceptors.filter((i) => {
        return !interceptorTypes.some((interceptor) => i instanceof interceptor);
      })
    );
  }

  private addInterceptor(interceptor: HttpInterceptor): HttpService {
    return new HttpService(this.injector, this.httpHandler, this.interceptors.concat([interceptor]));
  }

  private addInterceptors(interceptors: HttpInterceptor[]): HttpService {
    return new HttpService(this.injector, this.httpHandler, this.interceptors.concat([...interceptors]));
  }
}
